import React, { useState } from 'react';
import { getEventPageContactOrganizerUrl } from 'common/components/TouchpointEditor/utils';
import GuestCookieManager from 'common/compounds/CookieManagement/components/GuestCookieManager';
import { getUserCookieConsent } from 'common/compounds/CookieManagement/cookieTools';
import Calendar from 'common/icons/Calendar';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import {
    FooterContainer,
    ButtonGroup,
    FooterButton,
    CalendarWrapper,
    CalendarIconWrapper,
    CalendarLinks,
    Delimiter,
} from './GuestPageFooter.styles';
import GuestDoNotSellModal from '../DoNotSell/components/GuestDoNotSellModal';

const bootstrap = getBootstrap();
const uploadHash = bootstrap.get('uploadHash', '');
const eventBaseUrl = bootstrap.get('event.canonicalEventlUrl', '');

const cookieSettings = bootstrap.get('cookieSettings', {});
const donsSettings = bootstrap.get('doNotSellSettings', {});
const insideEditingIframe = bootstrap.get('splashPage.insideEditingIframe');
const isTakingScreenshot = bootstrap.get('isTakingScreenshot', false);

const hasCookieConsent = getUserCookieConsent(bootstrap.get('event.id')) !== null;

const canShowCookieBanner =
    cookieSettings?.enableCookieBanner && !insideEditingIframe && !isTakingScreenshot && !hasCookieConsent;

const GuestPageFooter = ({ isEditing = false, isPreview = false, showCalendar = false }) => {
    const [showCalendarLinks, setShowCalendarLinks] = useState(false);
    const [showCookieBanner, setShowCookieBanner] = useState(canShowCookieBanner && !isEditing);
    const [showDonsBanner, setShowDonsBanner] = useState(false);

    const contactTheOrganizerLink = getEventPageContactOrganizerUrl();
    const calLinkAppend = `&u=${uploadHash}`;
    const isEditingOrPreview = isEditing || isPreview;

    // the legacy page has different bootstrap data and returns the contact link without
    // https. So we can check if we are on a legacy page by checking if the link starts with http(s).
    // This helps because if we are in the legacy page we can open the contact modal directly
    const isLegacyPage = !/^https?:\/\//i.test(contactTheOrganizerLink);
    const isCookieBannerEnabled = cookieSettings?.enableCookieBanner;
    const isDonsBannerEnabled = donsSettings?.enabledDonsConsent;

    return (
        <>
            <FooterContainer>
                <ButtonGroup>
                    {isCookieBannerEnabled ? (
                        <FooterButton
                            as="button"
                            type="button"
                            disabled={isEditing}
                            onClick={() => setShowCookieBanner(true)}
                        >
                            Manage Cookies
                        </FooterButton>
                    ) : null}
                    {isDonsBannerEnabled && isCookieBannerEnabled ? <Delimiter>|</Delimiter> : null}
                    {isDonsBannerEnabled ? (
                        <GuestDoNotSellModal
                            open={showDonsBanner}
                            onOpenChange={setShowDonsBanner}
                            trigger={
                                <FooterButton as="button" type="button" disabled={isEditing}>
                                    {donsSettings?.footerText}
                                </FooterButton>
                            }
                            {...donsSettings}
                        />
                    ) : null}
                </ButtonGroup>
                <ButtonGroup>
                    {showCalendar && (
                        <CalendarWrapper>
                            <CalendarIconWrapper
                                aria-label="calendar icon"
                                role="button"
                                onClick={() => setShowCalendarLinks(!showCalendarLinks)}
                            >
                                <Calendar />
                            </CalendarIconWrapper>
                            <CalendarLinks show={showCalendarLinks}>
                                <a
                                    aria-label="gmail link"
                                    rel="nofollow noopener noreferrer"
                                    href={isEditingOrPreview ? '#' : `${eventBaseUrl}?calendar-Gmail${calLinkAppend}`}
                                    target={isEditingOrPreview ? '_self' : '_blank'}
                                    onClick={() => {
                                        if (window.gtag) {
                                            window.gtag('event', 'calendar_attachment', {
                                                event_category: 'CalGmailFooter',
                                            });
                                        }
                                    }}
                                >
                                    Google
                                </a>
                                <a
                                    aria-label="outlook calendar link"
                                    rel="nofollow noopener noreferrer"
                                    href={isEditingOrPreview ? '#' : `${eventBaseUrl}?calendar-Outlook${calLinkAppend}`}
                                    target={isEditingOrPreview ? '_self' : '_blank'}
                                    onClick={() => {
                                        if (window.gtag) {
                                            window.gtag('event', 'calendar_attachment', {
                                                event_category: 'CalOutlookFooter',
                                            });
                                        }
                                    }}
                                >
                                    Outlook
                                </a>
                                <a
                                    aria-label="apple icalendar link"
                                    rel="nofollow noopener noreferrer"
                                    href={isEditingOrPreview ? '#' : `${eventBaseUrl}?calendar-iCal${calLinkAppend}`}
                                    target={isEditingOrPreview ? '_self' : '_blank'}
                                    onClick={() => {
                                        if (window.gtag) {
                                            window.gtag('event', 'calendar_attachment', {
                                                event_category: 'CalMacFooter',
                                            });
                                        }
                                    }}
                                >
                                    iCal
                                </a>
                                <a
                                    aria-label="yahoo calendar link"
                                    rel="nofollow noopener noreferrer"
                                    href={isEditingOrPreview ? '#' : `${eventBaseUrl}?calendar-Yahoo${calLinkAppend}`}
                                    target={isEditingOrPreview ? '_self' : '_blank'}
                                    onClick={() => {
                                        if (window.gtag) {
                                            window.gtag('event', 'calendar_attachment', {
                                                event_category: 'CalYahooFooter',
                                            });
                                        }
                                    }}
                                >
                                    Yahoo
                                </a>
                            </CalendarLinks>
                        </CalendarWrapper>
                    )}
                    <FooterButton
                        href={isEditingOrPreview || isLegacyPage ? '#' : contactTheOrganizerLink}
                        onClick={(e) => {
                            if (
                                !isEditingOrPreview &&
                                isLegacyPage &&
                                window.splash &&
                                window.splash.ContactFormManager
                            ) {
                                e.preventDefault();
                                window.splash.ContactFormManager.openModal();
                            }
                        }}
                    >
                        CONTACT THE ORGANIZER
                    </FooterButton>
                </ButtonGroup>
            </FooterContainer>
            {showCookieBanner && <GuestCookieManager onUserConsent={() => setShowCookieBanner(false)} />}
        </>
    );
};

export default GuestPageFooter;
